import { useState } from "react";
import { UserPlus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { suggestCandidate } from "@/lib/scoutApi";
import { extractApiError } from "@/lib/api";

const PILLARS = ["Science", "Politics", "Philosophy", "Religion"];
const inputCls = "w-full border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[var(--accent)]";

export const SuggestGuest = ({ onCreated }) => {
  const [form, setForm] = useState({ name: "", role: "", affiliation: "", note: "", pillars: [], research: true });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const togglePillar = (p) =>
    setForm((f) => ({ ...f, pillars: f.pillars.includes(p) ? f.pillars.filter((x) => x !== p) : [...f.pillars, p] }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const c = await suggestCandidate(form);
      onCreated(c);
      toast.success(form.research ? `${c.name} added — the scout is researching them now` : `${c.name} added to the CRM`);
      setForm({ name: "", role: "", affiliation: "", note: "", pillars: [], research: true });
    } catch (err) {
      toast.error(extractApiError(err, "Could not add this person."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="scout-suggest" className="border border-slate-200 p-5">
      <div className="text-xs tracking-tight text-[var(--accent)] mb-1 flex items-center gap-2">
        <UserPlus className="w-3.5 h-3.5" /> Suggest a guest
      </div>
      <p className="text-xs text-slate-500 mb-4">Know someone the scout hasn&apos;t found? Drop the name — it&apos;ll build the full profile.</p>
      <input data-testid="scout-suggest-name" value={form.name} onChange={set("name")} required minLength={2} maxLength={160} placeholder="Full name *" className={inputCls} />
      <div className="mt-2 grid grid-cols-2 gap-2">
        <input data-testid="scout-suggest-role" value={form.role} onChange={set("role")} maxLength={200} placeholder="Role (optional)" className={inputCls} />
        <input data-testid="scout-suggest-affiliation" value={form.affiliation} onChange={set("affiliation")} maxLength={200} placeholder="Affiliation (optional)" className={inputCls} />
      </div>
      <textarea data-testid="scout-suggest-note" value={form.note} onChange={set("note")} rows={2} maxLength={1000}
        placeholder="Why them? Anything you already know (optional)" className={`mt-2 ${inputCls} resize-y`} />
      <div className="mt-3 flex flex-wrap gap-1" data-testid="scout-suggest-pillars">
        {PILLARS.map((p) => (
          <button key={p} type="button" onClick={() => togglePillar(p)} aria-pressed={form.pillars.includes(p)}
            className={`px-2.5 py-1 text-xs border transition-colors ${form.pillars.includes(p) ? "bg-[#0A192F] border-[#0A192F] text-white" : "border-slate-300 text-slate-500 hover:text-[#0A192F]"}`}>
            {p}
          </button>
        ))}
      </div>
      <label className="mt-3 flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
        <input type="checkbox" data-testid="scout-suggest-research" checked={form.research} onChange={(e) => setForm((f) => ({ ...f, research: e.target.checked }))} className="w-4 h-4 accent-[var(--accent)]" />
        <Sparkles className="w-3.5 h-3.5 text-[var(--accent)]" /> Let the scout research them (stance, reachability, questions, sources)
      </label>
      <button type="submit" data-testid="scout-suggest-submit" disabled={busy || form.name.trim().length < 2}
        className="mt-4 inline-flex items-center gap-2 bg-[#0A192F] hover:bg-[var(--accent)] disabled:opacity-50 text-white px-4 py-2 text-sm font-medium transition-colors">
        <UserPlus className="w-4 h-4" /> {busy ? "Adding…" : "Add to CRM"}
      </button>
    </form>
  );
};

export default SuggestGuest;
